import { API_BASE_URL, getAuthHeaders, handleApiError } from './apiUtils';

export interface FinanceCustomerOption {
    id: number;
    name: string;
}

export interface LedgerEntry {
    id: number;
    date: string;
    description: string;
    debit: number;
    credit: number;
    balance: number;
    type: string;
}

export interface LedgerSummary {
    customer: {
        id: number;
        loginId: string;
        name: string;
        city?: string;
    };
    dateRange: {
        from: string | null;
        to: string | null;
    };
    totals: {
        debit: number;
        credit: number;
    };
    entries: LedgerEntry[];
}

export interface LedgerResponse {
    data: LedgerSummary;
    customers: FinanceCustomerOption[];
}

export interface PaymentListItem {
    id: number;
    unique_id: string;
    amount: number;
    instrument_no: string;
    payment_date: string;
    payment_mode: string;
    attachment?: string | null;
    created_at: string | null;
    client?: {
        id: number;
        name: string;
    } | null;
}

export interface PaymentDetail extends PaymentListItem {
    consignments: Array<{
        id: number;
        consignment_no?: string;
        cod_amount: number;
        service_charges: number;
        tax: number;
        paid_amount: number;
        status: {
            id: number;
            name?: string;
            badge?: string;
        };
    }>;
    notes: Array<{
        id: number;
        unique_id: string;
        type: 'Debit' | 'Credit';
        amount: number;
        tax: number;
        created_at: string | null;
    }>;
}

export interface PaginatedResponse<T> {
    data: T[];
    meta: {
        pagination: {
            current_page: number;
            per_page: number;
            total: number;
            last_page: number;
        };
        customers: FinanceCustomerOption[];
    };
}

export interface PaymentCandidatesResponse {
    data: {
        consignments: Array<{
            id: number;
            consignment_no: string;
            created_at: string | null;
            status: {
                id: number;
                name?: string;
                badge?: string;
            };
            pricing: {
                cod_amount: number;
                service_charges: number;
                tax: number;
            };
        }>;
        notes: Array<{
            id: number;
            unique_id: string;
            type: 'Debit' | 'Credit';
            amount: number;
            tax: number;
            created_at: string | null;
        }>;
        totals: {
            codAmount: number;
            notesAdjustment: number;
            netPayable: number;
        };
    };
}

export interface CreatePaymentPayload {
    customerId?: number;
    consignmentIds: number[];
    noteIds?: number[];
    instrumentNo: string;
    paymentDate: string;
    paymentMode: 'Bank Deposit' | 'Cash in Hand' | 'IBFT';
    attachment?: File | null;
}

export interface NoteListItem {
    id: number;
    unique_id: string;
    amount: number;
    type: 'Debit' | 'Credit';
    tax: number;
    description: string;
    attachment?: string | null;
    created_at: string | null;
    client?: {
        id: number;
        name: string;
    } | null;
    created_by?: {
        id: number;
        name: string;
    } | null;
}

export interface CreateNotePayload {
    customerId: number;
    amount: number;
    type: 'Debit' | 'Credit';
    description: string;
    attachment?: File | null;
}

export interface InvoiceListItem {
    id: number;
    unique_id: string;
    type: string;
    date: string;
    customer?: {
        id: number;
        name: string;
    } | null;
    consignment_count?: number | null;
    service_fee_total?: number | null;
    download_url: string;
}

export interface InvoiceDetail extends InvoiceListItem {
    consignments: Array<{
        id: number;
        consignment_no: string;
        service_fee: number;
        tax: number;
        status: {
            id: number;
            name?: string;
            badge?: string;
        };
    }>;
    notes: Array<{
        id: number;
        unique_id: string;
        type: 'Debit' | 'Credit';
        amount: number;
        tax: number;
    }>;
}

const buildQueryString = (params: Record<string, string | number | undefined | null>) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            query.append(key, value.toString());
        }
    });
    const qs = query.toString();
    return qs ? `?${qs}` : '';
};

export const financeApi = {
    getLedger: async (params: { customerId?: number; fromDate?: string; toDate?: string } = {}): Promise<LedgerResponse> => {
        const queryString = buildQueryString({
            customer_id: params.customerId,
            from_date: params.fromDate,
            to_date: params.toDate,
        });

        const response = await fetch(`${API_BASE_URL}/finance/ledger${queryString}`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const payload = await response.json();
        return {
            data: payload.data as LedgerSummary,
            customers: payload.meta?.customers || [],
        };
    },

    getPayments: async (params: { customerId?: number; page?: number; perPage?: number } = {}): Promise<PaginatedResponse<PaymentListItem>> => {
        const queryString = buildQueryString({
            customer_id: params.customerId,
            page: params.page,
            per_page: params.perPage,
        });

        const response = await fetch(`${API_BASE_URL}/finance/payments${queryString}`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const payload = await response.json();
        return payload as PaginatedResponse<PaymentListItem>;
    },

    getPayment: async (paymentId: number): Promise<{ data: PaymentDetail }> => {
        const response = await fetch(`${API_BASE_URL}/finance/payments/${paymentId}`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        return (await response.json()) as { data: PaymentDetail };
    },

    getPaymentCandidates: async (params: { customerId?: number; fromDate?: string; toDate?: string } = {}): Promise<PaymentCandidatesResponse> => {
        const queryString = buildQueryString({
            customer_id: params.customerId,
            from_date: params.fromDate,
            to_date: params.toDate,
        });

        const response = await fetch(`${API_BASE_URL}/finance/payments/candidates${queryString}`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        return (await response.json()) as PaymentCandidatesResponse;
    },

    createPayment: async (payload: CreatePaymentPayload): Promise<{ data: PaymentDetail }> => {
        const formData = new FormData();

        if (payload.customerId) formData.append('customer_id', payload.customerId.toString());
        payload.consignmentIds.forEach(id => formData.append('consignment_ids[]', id.toString()));
        payload.noteIds?.forEach(id => formData.append('note_ids[]', id.toString()));

        formData.append('instrument_no', payload.instrumentNo);
        formData.append('payment_date', payload.paymentDate);
        formData.append('payment_mode', payload.paymentMode);

        if (payload.attachment) {
            formData.append('attachment', payload.attachment);
        }

        const headers = new Headers(getAuthHeaders());
        // Let browser set multipart boundary for FormData
        headers.delete('Content-Type');

        const response = await fetch(`${API_BASE_URL}/finance/payments`, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        return (await response.json()) as { data: PaymentDetail };
    },

    getNotes: async (params: { customerId?: number; page?: number; perPage?: number } = {}): Promise<PaginatedResponse<NoteListItem>> => {
        const queryString = buildQueryString({
            customer_id: params.customerId,
            page: params.page,
            per_page: params.perPage,
        });

        const response = await fetch(`${API_BASE_URL}/finance/notes${queryString}`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const payload = await response.json();
        return payload as PaginatedResponse<NoteListItem>;
    },

    createNote: async (payload: CreateNotePayload): Promise<{ data: NoteListItem }> => {
        const formData = new FormData();
        formData.append('customer_id', payload.customerId.toString());
        formData.append('amount', payload.amount.toString());
        formData.append('type', payload.type);
        formData.append('description', payload.description);

        if (payload.attachment) {
            formData.append('attachment', payload.attachment);
        }

        const headers = new Headers(getAuthHeaders());
        headers.delete('Content-Type');

        const response = await fetch(`${API_BASE_URL}/finance/notes`, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        return (await response.json()) as { data: NoteListItem };
    },

    getInvoices: async (params: { customerId?: number; page?: number; perPage?: number } = {}): Promise<PaginatedResponse<InvoiceListItem>> => {
        const queryString = buildQueryString({
            customer_id: params.customerId,
            page: params.page,
            per_page: params.perPage,
        });

        const response = await fetch(`${API_BASE_URL}/finance/invoices${queryString}`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const payload = await response.json();
        return payload as PaginatedResponse<InvoiceListItem>;
    },

    getInvoice: async (invoiceId: number): Promise<{ data: InvoiceDetail }> => {
        const response = await fetch(`${API_BASE_URL}/finance/invoices/${invoiceId}`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        return (await response.json()) as { data: InvoiceDetail };
    },

    downloadInvoice: async (invoiceId: number): Promise<Blob> => {
        const response = await fetch(`${API_BASE_URL}/finance/invoices/${invoiceId}/download`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        return await response.blob();
    },
};

